import React from 'react';
import { TrendingUp, TrendingDown, ArrowUpDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { Header } from '../components/Layout/Header';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';

const chartData = [
  { time: '00:00', price: 0.16 },
  { time: '04:00', price: 0.17 },
  { time: '08:00', price: 0.18 },
  { time: '12:00', price: 0.19 },
  { time: '16:00', price: 0.18 },
  { time: '20:00', price: 0.20 },
  { time: '24:00', price: 0.21 },
];

const topTokens = [
  { symbol: 'ALGO', name: 'Algorand', price: 0.21, change: 12.5, volume: '2.1M' },
  { symbol: 'USDC', name: 'USD Coin', price: 1.00, change: 0.1, volume: '5.8M' },
  { symbol: 'ARC', name: 'ARC-200', price: 0.05, change: -2.3, volume: '890K' },
  { symbol: 'GOLD', name: 'Gold Token', price: 1850.50, change: 1.8, volume: '1.2M' },
];

export const Trade: React.FC = () => {
  const [selectedToken, setSelectedToken] = React.useState('ALGO');
  const [tradeType, setTradeType] = React.useState<'buy' | 'sell'>('buy');

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header title="Trade" showProfile={false} />
      
      {/* Price Chart */}
      <div className="p-4">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">ALGO/USD</h2>
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-gray-900">$0.21</span>
                <div className="flex items-center text-green-600">
                  <TrendingUp size={16} className="mr-1" />
                  <span className="text-sm">+12.5%</span>
                </div>
              </div>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p>24h High: $0.22</p>
              <p>24h Low: $0.18</p>
            </div>
          </div>
          
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="time" axisLine={false} tickLine={false} />
                <YAxis hide />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Trading Interface */}
      <div className="px-4 mb-6">
        <Card>
          <div className="flex mb-4">
            <button
              onClick={() => setTradeType('buy')}
              className={`flex-1 py-2 px-4 rounded-l-lg font-medium ${
                tradeType === 'buy'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Buy
            </button>
            <button
              onClick={() => setTradeType('sell')}
              className={`flex-1 py-2 px-4 rounded-r-lg font-medium ${
                tradeType === 'sell'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Sell
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (ALGO)
              </label>
              <input
                type="number"
                placeholder="0.00"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex items-center justify-center">
              <ArrowUpDown size={20} className="text-gray-400" />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receive (USD)
              </label>
              <input
                type="number"
                placeholder="0.00"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Rate</span>
                <span className="font-medium">1 ALGO = $0.21</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Fee</span>
                <span className="font-medium">0.25%</span>
              </div>
            </div>
            
            <Button
              fullWidth
              size="lg"
              className={tradeType === 'buy' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {tradeType === 'buy' ? 'Buy ALGO' : 'Sell ALGO'}
            </Button>
          </div>
        </Card>
      </div>

      {/* Top Tokens */}
      <div className="px-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Tokens</h3>
        <div className="space-y-3">
          {topTokens.map((token) => (
            <Card key={token.symbol} hover>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="font-bold text-primary-600 text-sm">
                      {token.symbol.slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{token.symbol}</p>
                    <p className="text-sm text-gray-500">{token.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${token.price}</p>
                  <div className="flex items-center">
                    {token.change >= 0 ? (
                      <TrendingUp size={12} className="text-green-600 mr-1" />
                    ) : (
                      <TrendingDown size={12} className="text-red-600 mr-1" />
                    )}
                    <span className={`text-sm ${token.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {token.change >= 0 ? '+' : ''}{token.change}%
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};